import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { resolveCliAssetPath } from '@core-engine/utils/cli-root.js'
import { safeWriteFile } from '@core-engine/utils/fs.js'
import { detectProjectName } from '@next-adapter/config/detect.js'
import {
  applyAdminNamespaceToContent,
  applyAdminNamespaceToPath,
  resolveAdminNamespace
} from '@next-adapter/config/namespace.js'
import { adminDataTemplate } from '@next-adapter/init/templates/app/admin.js'
import { applyNextCacheApiForVersion, readTemplate } from '@next-adapter/template-reader.js'
import fs from 'fs-extra'

export interface ComponentScaffoldOptions {
  cwd: string
  config: ResolvedBetterstartConfig
}

/**
 * Copy all Admin components, hooks, types, utils, data, and lib code into the project.
 */
export function scaffoldComponents({ cwd, config }: ComponentScaffoldOptions): string[] {
  const admin = path.resolve(cwd, config.paths.admin)
  const created: string[] = []
  const namespace = config.frameworkConfig.next.namespace

  function write(relPath: string, content: string): void {
    const namespacedRelPath = applyAdminNamespaceToPath(relPath, namespace)
    const fullPath = path.join(admin, namespacedRelPath)
    const versionedContent = applyNextCacheApiForVersion(content, config.nextMajorVersion)
    if (safeWriteFile(fullPath, applyAdminNamespaceToContent(versionedContent, namespace))) {
      created.push(path.join(config.paths.admin, namespacedRelPath))
    }
  }

  // --- CSS ---
  write('admin-globals.css', readTemplate('admin-globals.css'))

  // --- Components: layouts ---
  write(
    'components/layouts/admin-providers.tsx',
    readTemplate('components/layouts/admin-providers.tsx')
  )
  write(
    'components/layouts/admin-nav-link.tsx',
    readTemplate('components/layouts/admin-nav-link.tsx')
  )
  write(
    'components/layouts/admin-sidebar.tsx',
    readTemplate('components/layouts/admin-sidebar.tsx')
  )
  write(
    'components/layouts/admin-sidebar-branding-rsc.tsx',
    readTemplate('components/layouts/admin-sidebar-branding-rsc.tsx')
  )
  write(
    'components/layouts/admin-sidebar-branding-skeleton.tsx',
    readTemplate('components/layouts/admin-sidebar-branding-skeleton.tsx')
  )
  write(
    'components/layouts/admin-sidebar-nav-link.tsx',
    readTemplate('components/layouts/admin-sidebar-nav-link.tsx')
  )
  write(
    'components/layouts/admin-sidebar-nav-link-skeleton.tsx',
    readTemplate('components/layouts/admin-sidebar-nav-link-skeleton.tsx')
  )
  write(
    'components/layouts/admin-settings-sidebar.tsx',
    readTemplate('components/layouts/admin-settings-sidebar.tsx')
  )
  write(
    'components/layouts/admin-sidebar-user-menu.tsx',
    readTemplate('components/layouts/admin-sidebar-user-menu.tsx')
  )
  write(
    'components/layouts/admin-sidebar-user-menu-skeleton.tsx',
    readTemplate('components/layouts/admin-sidebar-user-menu-skeleton.tsx')
  )
  write(
    'components/layouts/sidebar-branding.tsx',
    readTemplate('components/layouts/sidebar-branding.tsx')
  )
  write('components/layouts/admin-header.tsx', readTemplate('components/layouts/admin-header.tsx'))
  write(
    'components/layouts/content-skeleton.tsx',
    readTemplate('components/layouts/content-skeleton.tsx')
  )
  // --- Components: shared ---
  write('components/shared/page-header.tsx', readTemplate('components/shared/page-header.tsx'))
  write(
    'components/shared/sort-indicator.tsx',
    readTemplate('components/shared/sort-indicator.tsx')
  )
  write(
    'components/shared/dev-mode-integrate.tsx',
    readTemplate('components/shared/dev-mode-integrate.tsx')
  )
  write(
    'components/shared/dev-mode/copyable-code-block.tsx',
    readTemplate('components/shared/dev-mode/copyable-code-block.tsx')
  )
  write(
    'components/shared/dev-mode/dev-mode-code-mirror.tsx',
    readTemplate('components/shared/dev-mode/dev-mode-code-mirror.tsx')
  )
  write(
    'components/shared/dev-mode/dev-mode-types.ts',
    readTemplate('components/shared/dev-mode/dev-mode-types.ts')
  )
  write(
    'components/shared/dev-mode/plain-code-fallback.tsx',
    readTemplate('components/shared/dev-mode/plain-code-fallback.tsx')
  )
  write(
    'components/shared/dev-mode/lifecycle-hooks-tab.tsx',
    readTemplate('components/shared/dev-mode/lifecycle-hooks-tab.tsx')
  )
  write(
    'components/shared/dev-mode/snippets-tab.tsx',
    readTemplate('components/shared/dev-mode/snippets-tab.tsx')
  )
  write('components/shared/delete-dialog.tsx', readTemplate('components/shared/delete-dialog.tsx'))
  write(
    'components/shared/entity-metadata.tsx',
    readTemplate('components/shared/entity-metadata.tsx')
  )
  write(
    'components/shared/entity-versions/entity-versions-button.tsx',
    readTemplate('components/shared/entity-versions/entity-versions-button.tsx')
  )
  write(
    'components/shared/entity-versions/entity-versions-drawer.tsx',
    readTemplate('components/shared/entity-versions/entity-versions-drawer.tsx')
  )
  write(
    'components/shared/entity-versions/entity-versions-current-row.tsx',
    readTemplate('components/shared/entity-versions/entity-versions-current-row.tsx')
  )
  write(
    'components/shared/entity-versions/entity-version-item.tsx',
    readTemplate('components/shared/entity-versions/entity-version-item.tsx')
  )
  write(
    'components/shared/entity-versions/entity-version-restore-dialog.tsx',
    readTemplate('components/shared/entity-versions/entity-version-restore-dialog.tsx')
  )
  write(
    'components/shared/entity-filters-bar.tsx',
    readTemplate('components/shared/entity-filters-bar.tsx')
  )
  write('components/shared/search-input.tsx', readTemplate('components/shared/search-input.tsx'))
  write(
    'components/shared/sort-order-dialog.tsx',
    readTemplate('components/shared/sort-order-dialog.tsx')
  )
  write(
    'components/shared/sort-order-drag-overlay-item.tsx',
    readTemplate('components/shared/sort-order-drag-overlay-item.tsx')
  )
  write(
    'components/shared/sort-order-item.tsx',
    readTemplate('components/shared/sort-order-item.tsx')
  )
  write(
    'components/shared/sort-order-types.ts',
    readTemplate('components/shared/sort-order-types.ts')
  )
  // --- Components: shared/media ---
  write(
    'components/shared/media/media-preview.tsx',
    readTemplate('components/shared/media/media-preview.tsx')
  )
  write(
    'components/shared/media/media-grid-item.tsx',
    readTemplate('components/shared/media/media-grid-item.tsx')
  )
  write(
    'components/shared/media/media-grid.tsx',
    readTemplate('components/shared/media/media-grid.tsx')
  )
  write(
    'components/shared/media/media-empty-state.tsx',
    readTemplate('components/shared/media/media-empty-state.tsx')
  )
  write(
    'components/shared/media/edit-media-dialog.tsx',
    readTemplate('components/shared/media/edit-media-dialog.tsx')
  )
  write(
    'components/shared/media/edit-media-dialog-content.tsx',
    readTemplate('components/shared/media/edit-media-dialog-content.tsx')
  )
  write(
    'components/shared/media/media-delete-drawer.tsx',
    readTemplate('components/shared/media/media-delete-drawer.tsx')
  )
  write(
    'components/shared/media/media-delete-dialog.tsx',
    readTemplate('components/shared/media/media-delete-dialog.tsx')
  )
  write(
    'components/shared/media/media-gallery-dialog.tsx',
    readTemplate('components/shared/media/media-gallery-dialog.tsx')
  )
  write(
    'components/shared/media/media-filters-bar.tsx',
    readTemplate('components/shared/media/media-filters-bar.tsx')
  )
  write(
    'components/shared/media/media-url-importer.tsx',
    readTemplate('components/shared/media/media-url-importer.tsx')
  )
  write(
    'components/shared/media/media-grid-pagination.tsx',
    readTemplate('components/shared/media/media-grid-pagination.tsx')
  )
  // --- Components: shared/data-table ---
  write(
    'components/shared/data-table/data-table.tsx',
    readTemplate('components/shared/data-table/data-table.tsx')
  )
  write(
    'components/shared/data-table/data-grid.tsx',
    readTemplate('components/shared/data-table/data-grid.tsx')
  )
  write(
    'components/shared/data-table/data-table-pagination.tsx',
    readTemplate('components/shared/data-table/data-table-pagination.tsx')
  )
  // --- Components: ui (shadcn registry raw file copy) ---
  const uiCreated = copyUiTemplates(cwd, config)
  created.push(...uiCreated)

  // --- Components: custom (BetterStart raw file copy) ---
  const customCreated = copyCustomTemplates(cwd, config)
  created.push(...customCreated)

  // --- Core types ---
  write('types/index.ts', readTemplate('types/index.ts'))
  write('types/auth.ts', readTemplate('types/auth.ts'))
  write('types/navigation.ts', readTemplate('types/navigation.ts'))
  write('types/table-meta.ts', readTemplate('types/table-meta.ts'))
  write('types/webhooks.ts', readTemplate('types/webhooks.ts'))

  // --- Domain utils ---
  for (const relPath of [
    'utils/auth/roles.ts',
    'utils/audit/audit.ts',
    'utils/date/date.ts',
    'utils/dev-mode/code-block-height.ts',
    'utils/editor/content-editor.ts',
    'utils/editor/content-editor-rich-extensions.ts',
    'utils/editor/editor-view.ts',
    'utils/editor/markdown.ts',
    'utils/editor/node-attrs.ts',
    'utils/editor/source-media.ts',
    'utils/editor/slash-commands.ts',
    'utils/editor/table-add-controls.ts',
    'utils/editor/table-bubble.ts',
    'utils/editor/table-input.ts',
    'utils/editor/task-item.ts',
    'utils/editor/tiptap.ts',
    'utils/email/form-delivery.ts',
    'utils/form/list-field.ts',
    'utils/media/fallback.ts',
    'utils/media/media.ts',
    'utils/media/query.ts',
    'utils/navigation/order.ts',
    'utils/navigation/sidebar.ts',
    'utils/page/boundary.ts',
    'utils/seo/seo.ts',
    'utils/shared/cn.ts',
    'utils/storage/local.ts',
    'utils/table/table.ts',
    'utils/text/text.ts',
    'utils/theme/system.ts',
    'utils/upload/remote.ts',
    'utils/upload/upload.ts',
    'utils/user/user.ts',
    'utils/validation/validation.ts',
    'utils/webhook/signature.ts'
  ]) {
    write(relPath, readTemplate(relPath))
  }

  // --- Hooks ---
  write('hooks/use-upload.ts', readTemplate('hooks/use-upload.ts'))
  write(
    'hooks/content-editor/use-blockquote.ts',
    readTemplate('hooks/content-editor/use-blockquote.ts')
  )
  write(
    'hooks/content-editor/use-code-block.ts',
    readTemplate('hooks/content-editor/use-code-block.ts')
  )
  write(
    'hooks/content-editor/use-color-highlight.ts',
    readTemplate('hooks/content-editor/use-color-highlight.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-source-mode.tsx',
    readTemplate('hooks/content-editor/use-content-editor-source-mode.tsx')
  )
  write(
    'hooks/content-editor/use-content-editor.ts',
    readTemplate('hooks/content-editor/use-content-editor.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-media-insertion.ts',
    readTemplate('hooks/content-editor/use-content-editor-media-insertion.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-table-add-controls.ts',
    readTemplate('hooks/content-editor/use-content-editor-table-add-controls.ts')
  )
  write(
    'hooks/content-editor/use-table-button.ts',
    readTemplate('hooks/content-editor/use-table-button.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-mobile-toolbar.ts',
    readTemplate('hooks/content-editor/use-content-editor-mobile-toolbar.ts')
  )
  write(
    'hooks/content-editor/use-content-editor-slash-menu.ts',
    readTemplate('hooks/content-editor/use-content-editor-slash-menu.ts')
  )
  write('hooks/content-editor/use-heading.ts', readTemplate('hooks/content-editor/use-heading.ts'))
  write(
    'hooks/content-editor/use-heading-dropdown-menu.ts',
    readTemplate('hooks/content-editor/use-heading-dropdown-menu.ts')
  )
  write(
    'hooks/content-editor/use-is-breakpoint.ts',
    readTemplate('hooks/content-editor/use-is-breakpoint.ts')
  )
  write(
    'hooks/content-editor/use-link-popover.ts',
    readTemplate('hooks/content-editor/use-link-popover.ts')
  )
  write('hooks/content-editor/use-list.ts', readTemplate('hooks/content-editor/use-list.ts'))
  write(
    'hooks/content-editor/use-list-dropdown-menu.ts',
    readTemplate('hooks/content-editor/use-list-dropdown-menu.ts')
  )
  write('hooks/content-editor/use-mark.ts', readTemplate('hooks/content-editor/use-mark.ts'))
  write(
    'hooks/content-editor/use-menu-navigation.ts',
    readTemplate('hooks/content-editor/use-menu-navigation.ts')
  )
  write(
    'hooks/content-editor/use-undo-redo.ts',
    readTemplate('hooks/content-editor/use-undo-redo.ts')
  )
  write(
    'hooks/content-editor/use-tiptap-editor.ts',
    readTemplate('hooks/content-editor/use-tiptap-editor.ts')
  )
  write('hooks/use-copy-to-clipboard.ts', readTemplate('hooks/use-copy-to-clipboard.ts'))
  write('hooks/use-dev-mode-integration.ts', readTemplate('hooks/use-dev-mode-integration.ts'))
  write('hooks/use-local-storage.ts', readTemplate('hooks/use-local-storage.ts'))
  write('hooks/use-admin-theme.tsx', readTemplate('hooks/use-admin-theme.tsx'))
  write('hooks/use-audit-log.ts', readTemplate('hooks/use-audit-log.ts'))
  write('hooks/use-users.ts', readTemplate('hooks/use-users.ts'))
  write('hooks/use-webhooks.ts', readTemplate('hooks/use-webhooks.ts'))
  write('hooks/use-mobile.ts', readTemplate('hooks/use-mobile.ts'))
  write('hooks/use-failed-image-url.ts', readTemplate('hooks/use-failed-image-url.ts'))
  write('hooks/use-media.ts', readTemplate('hooks/use-media.ts'))
  write('hooks/use-entity-versions.ts', readTemplate('hooks/use-entity-versions.ts'))
  write('hooks/use-media-filters-bar.ts', readTemplate('hooks/use-media-filters-bar.ts'))
  write('hooks/use-table-utils.ts', readTemplate('hooks/use-table-utils.ts'))
  write(
    'hooks/use-page-boundary-blur-visibility.ts',
    readTemplate('hooks/use-page-boundary-blur-visibility.ts')
  )
  write('hooks/use-page-scroll-threshold.ts', readTemplate('hooks/use-page-scroll-threshold.ts'))

  // --- Core data/navigation ---
  const adminNamespace = resolveAdminNamespace(namespace)
  const projectName = detectProjectName(cwd)
  write('data/navigation.ts', readTemplate('data/navigation.ts'))
  write('data/webhook-events.ts', readTemplate('data/webhook-events.ts'))
  write(`utils/app/${adminNamespace.camel}.ts`, adminDataTemplate(projectName, namespace))

  // --- Lib ---
  for (const relPath of [
    'lib/actions/forms/types.ts',
    'lib/actions/forms/get-form-settings.ts',
    'lib/actions/forms/upsert-form-settings.ts',
    'lib/actions/forms/get-all-form-settings.ts',
    'lib/actions/forms/index.ts',
    'lib/actions/webhooks/types.ts',
    'lib/actions/webhooks/internal-get-active-endpoints.ts',
    'lib/actions/webhooks/dispatch.ts',
    'lib/actions/webhooks/get-webhooks.ts',
    'lib/actions/webhooks/create-webhook.ts',
    'lib/actions/webhooks/update-webhook.ts',
    'lib/actions/webhooks/delete-webhook.ts',
    'lib/actions/webhooks/get-webhook-secret.ts',
    'lib/actions/webhooks/regenerate-webhook-secret.ts',
    'lib/actions/webhooks/get-webhook-subscriptions.ts',
    'lib/actions/webhooks/send-test-webhook.ts',
    'lib/actions/webhooks/get-webhook-deliveries.ts',
    'lib/actions/webhooks/register.ts',
    'lib/actions/webhooks/index.ts',
    'lib/actions/entity-versions/types.ts',
    'lib/actions/entity-versions/get-entity-versions.ts',
    'lib/actions/entity-versions/internal-create-entity-version.ts',
    'lib/actions/entity-versions/internal-delete-entity-versions.ts',
    'lib/actions/entity-versions/index.ts',
    'lib/actions/audit/types.ts',
    'lib/actions/audit/request-metadata.ts',
    'lib/actions/audit/record-audit-event.ts',
    'lib/actions/audit/register.ts',
    'lib/actions/audit/get-audit-logs.ts',
    'lib/actions/audit/cleanup-audit-logs.ts',
    'lib/actions/audit/index.ts',
    'lib/lifecycle-hooks/types.ts',
    'lib/lifecycle-hooks/registry.ts',
    'lib/lifecycle-hooks/runner.ts',
    'lib/lifecycle-hooks/register.ts',
    'lib/lifecycle-hooks/register.local.ts',
    'lib/lifecycle-hooks/index.ts',
    'lib/actions/upload/types.ts',
    'lib/actions/upload/upload-files.ts',
    'lib/actions/upload/upload-file.ts',
    'lib/actions/upload/upload-media-from-url.ts',
    'lib/actions/upload/upload-image-from-url.ts',
    'lib/actions/upload/index.ts',
    'lib/actions/users/types.ts',
    'lib/actions/users/change-user-password.ts',
    'lib/actions/users/create-user.ts',
    'lib/actions/users/get-users.ts',
    'lib/actions/users/update-user-role.ts',
    'lib/actions/users/delete-user.ts',
    'lib/actions/users/index.ts',
    'lib/actions/media/types.ts',
    'lib/actions/media/get-media.ts',
    'lib/actions/media/get-media-by-id.ts',
    'lib/actions/media/get-media-by-ids.ts',
    'lib/actions/media/create-media.ts',
    'lib/actions/media/update-media.ts',
    'lib/actions/media/delete-media.ts',
    'lib/actions/media/delete-media-bulk.ts',
    'lib/actions/media/index.ts',
    'lib/actions/profile/types.ts',
    'lib/actions/profile/change-own-password.ts',
    'lib/actions/profile/is-email-configured.ts',
    'lib/actions/profile/invalidate-users-cache.ts',
    'lib/actions/profile/update-email.ts',
    'lib/actions/profile/index.ts'
  ]) {
    write(relPath, readTemplate(relPath))
  }
  for (const relPath of [
    'lib/actions/email/index.ts',
    'lib/actions/email/provider.ts',
    'lib/actions/email/types.ts',
    'lib/actions/email/none.ts',
    'lib/actions/email/is-email-delivery-configured.ts',
    'lib/actions/email/send-email.ts',
    'lib/actions/email/send-password-reset-email.ts',
    'lib/actions/email/form-delivery.ts',
    'lib/actions/storage/index.ts',
    'lib/actions/storage/provider.ts',
    'lib/actions/storage/types.ts',
    'lib/actions/storage/local.ts',
    'lib/actions/storage/save-upload.ts'
  ]) {
    write(relPath, readTemplate(relPath))
  }

  // --- Schema metaschema ---
  const schemaCreated = copySchemaMetaschema(cwd, config)
  created.push(...schemaCreated)

  return created
}

/**
 * Copy raw shadcn registry UI component files from the shared React Admin asset pack.
 */
function copyUiTemplates(cwd: string, config: ResolvedBetterstartConfig): string[] {
  return copyStaticComponentTemplates(cwd, config, 'ui', 'ui')
}

/**
 * Copy raw BetterStart custom component files from the shared React Admin asset pack.
 */
function copyCustomTemplates(cwd: string, config: ResolvedBetterstartConfig): string[] {
  return copyStaticComponentTemplates(cwd, config, 'custom', 'custom')
}

function copyStaticComponentTemplates(
  cwd: string,
  config: ResolvedBetterstartConfig,
  sourceDirectory: 'ui' | 'custom',
  targetDirectory: 'ui' | 'custom'
): string[] {
  const created: string[] = []
  const destDir = path.resolve(cwd, config.paths.admin, 'components', targetDirectory)

  const srcDir = resolveCliAssetPath('shared-assets', 'react-admin', sourceDirectory)

  if (!fs.existsSync(srcDir)) {
    return created
  }

  fs.ensureDirSync(destDir)

  if (sourceDirectory === 'custom') {
    copyStaticComponentTemplatesRecursive(
      srcDir,
      destDir,
      '',
      path.join(config.paths.admin, 'components', targetDirectory),
      config.frameworkConfig.next.namespace,
      created
    )
    return created
  }

  const files = fs
    .readdirSync(srcDir, { withFileTypes: true })
    .filter(
      (entry) => entry.isFile() && (entry.name.endsWith('.tsx') || entry.name.endsWith('.ts'))
    )
    .map((entry) => entry.name)

  for (const file of files) {
    const namespacedFile = applyAdminNamespaceToPath(file, config.frameworkConfig.next.namespace)
    const destPath = path.join(destDir, namespacedFile)
    if (!fs.existsSync(destPath)) {
      const content = fs.readFileSync(path.join(srcDir, file), 'utf-8')
      fs.writeFileSync(
        destPath,
        applyAdminNamespaceToContent(content, config.frameworkConfig.next.namespace),
        'utf-8'
      )
      created.push(path.join(config.paths.admin, 'components', targetDirectory, namespacedFile))
    }
  }

  return created
}

function copyStaticComponentTemplatesRecursive(
  srcDir: string,
  destDir: string,
  relBase: string,
  createdPrefix: string,
  namespace: string,
  created: string[]
): void {
  const entries = fs.readdirSync(srcDir, { withFileTypes: true })
  for (const entry of entries) {
    const rel = path.join(relBase, entry.name)
    const srcPath = path.join(srcDir, entry.name)
    const destPath = path.join(destDir, entry.name)

    if (entry.isDirectory()) {
      const namespacedDestPath = path.join(
        destDir,
        applyAdminNamespaceToPath(entry.name, namespace)
      )
      copyStaticComponentTemplatesRecursive(
        srcPath,
        namespacedDestPath,
        rel,
        createdPrefix,
        namespace,
        created
      )
      continue
    }

    if (!entry.name.endsWith('.tsx') && !entry.name.endsWith('.ts')) {
      continue
    }

    const namespacedRel = applyAdminNamespaceToPath(rel, namespace)
    const namespacedDestPath = path.join(
      path.dirname(destPath),
      applyAdminNamespaceToPath(entry.name, namespace)
    )
    if (!fs.existsSync(namespacedDestPath)) {
      fs.ensureDirSync(path.dirname(namespacedDestPath))
      const content = fs.readFileSync(srcPath, 'utf-8')
      fs.writeFileSync(
        namespacedDestPath,
        applyAdminNamespaceToContent(content, namespace),
        'utf-8'
      )
      created.push(path.join(createdPrefix, namespacedRel))
    }
  }
}

/**
 * Copy schema.json metaschema into admin/schemas/ so users get IDE validation.
 */
function copySchemaMetaschema(cwd: string, config: ResolvedBetterstartConfig): string[] {
  const created: string[] = []
  const srcPath = resolveCliAssetPath('shared-assets', 'react-admin', 'schema.json')
  const destPath = path.resolve(cwd, config.paths.schemas, 'schema.json')

  if (fs.existsSync(srcPath) && !fs.existsSync(destPath)) {
    fs.ensureDirSync(path.dirname(destPath))
    fs.copyFileSync(srcPath, destPath)
    created.push(path.join(config.paths.schemas, 'schema.json'))
  }

  return created
}
